<template>
	<v-card height="800px" class="position-relative overflow-auto">
		<v-overlay
			:model-value="pdfLoading"
			persistent
			contained
			absolute
			class="d-flex align-center justify-center position-absolute"
		>
			<v-progress-circular indeterminate color="primary" />
		</v-overlay>
		<v-container class="d-flex align-center justify-end ga-2 pa-0">
			<v-container
				v-if="!pdfLoading"
				class="d-flex align-center justify-start ga-2 pa-2"
			>
				<span class="text-caption">
					Page {{ currentPage }} of {{ totalPages ?? "?" }}
				</span>
			</v-container>
			<v-container class="d-flex align-center justify-end ga-2 pa-0">
				<v-btn
					:disabled="zoom <= minZoom"
					variant="flat"
					size="small"
					class="bg-surface-tint text-fg-1"
					@click="zoomOut"
				>
					<v-icon size="x-large">mdi-magnify-minus-outline</v-icon>
				</v-btn>
				<div class="zoom-input-wrap">
					<v-text-field
						v-model.trim="zoomInput"
						class="text-caption zoom-input"
						density="compact"
						hide-details
						type="text"
						inputmode="numeric"
						pattern="[0-9]*"
						suffix="%"
						@blur="applyZoomInput"
						@keyup.enter="applyZoomInput"
					/>
				</div>
				<v-btn
					:disabled="zoom >= maxZoom"
					variant="flat"
					size="small"
					class="bg-surface-tint text-fg-1"
					@click="zoomIn"
				>
					<v-icon size="x-large">mdi-magnify-plus-outline</v-icon>
				</v-btn>
			</v-container>
		</v-container>
		<v-select
			v-if="pdfFiles.length > 1"
			v-model="currentFile"
			:items="pdfSelectData"
			item-title="text"
			item-value="value"
			:item-props="getFileProps"
			label="Select file"
			:persistent-hint="true"
			density="compact"
			hide-details
			@update:model-value="handleFileChange"
		/>

		<v-container class="position-relative pa-0 ma-0" flat>
			<v-crosshairs
				ref="crosshairs"
				:color="crosshairColor"
				:line-width="crosshairLineWidth"
				:size="crosshairSize"
				:left="crosshairViewport.left"
				:top="crosshairViewport.top"
				:pulse="true"
			/>
			<PDF
				ref="pdf"
				:key="`pdf-${currentFile}`"
				:src="fileData"
				:page="currentPage"
				:pdf-width="pdfWidth"
				@on-scroll="scrollPdf"
				@on-pdf-init="initPdf"
				@on-page-change="pageChange"
			/>
		</v-container>
	</v-card>
</template>

<script>
import PDF from "pdf-vue3";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import VCrosshairs from "@/components/VCrosshairs";
import _ from "lodash";

export default {
	name: "PdfViewer",
	components: {
		PDF,
		VCrosshairs,
	},

	props: {
		paperUuid: {
			type: String,
			default: "",
		},
		sdeUuid: {
			type: String,
			default: "",
		},
		dataPointUuid: {
			type: String,
			default: "",
		},
		selectedNode: {
			type: Object,
			default: () => ({}),
		},
	},

	data() {
		return {
			crosshairViewport: { left: 0, top: 0 },
			currentFile: null,
			currentPage: 1,
			crosshairColor: "#ff1744",
			crosshairLineWidth: 2,
			crosshairSize: 64,
			zoomInput: "100",
			maxZoom: 5,
			minZoom: 0.5,
			pageYMargin: 8,
			pageXMargin: 2,
			pdfFiles: [],
			pdfLoading: false,
			scrollLeftPos: 0,
			scrollTopPos: 0,
			sdePaperUuid: null,
			totalPages: 1,
			wpdScaleRatio: 3,
			zoom: 1,
			zoomStep: 0.2,
		};
	},

	computed: {
		currentPageIndex() {
			return Math.max(0, (this.currentPage || 1) - 1);
		},
		pdfWidth() {
			return `${this.zoom * 100}%`;
		},
		fileData() {
			return this.pdfFiles[this.currentFile]
				? this.pdfFiles[this.currentFile].data
				: null;
		},
		fileType() {
			return this.pdfFiles[this.currentFile]
				? this.pdfFiles[this.currentFile].type
				: null;
		},
		pdfSelectData() {
			return this.pdfFiles.map((file, index) => ({
				text: file.name,
				value: index,
				disabled: file.data === null,
			}));
		},
	},

	watch: {
		async selectedNode(val) {
			if (!val) {
				this.$refs.crosshairs.clearCrosshairs();
			} else if (val.isDataPoint) {
				this.pdfLoading = true;
				this.$refs.crosshairs.clearCrosshairs();
				let isFileChange = false;

				// Changing file
				if (!_.isNil(val.file) && val.file !== this.currentFile) {
					this.currentFile = val.file;
					this.currentPage = val.page ?? 1;
					this.fileChangeData = val;
					isFileChange = true;
				}
				// Just changing page
				else if (!_.isNil(val.page) && val.page !== this.currentPage) {
					this.currentPage = val.page ?? 1;
				}

				await this.$nextTick();

				// Have to hackily wait for canvas to be stable after file change
				if (isFileChange) {
					await this.waitForCanvasStable(this.currentPageIndex);
				}
				await this.$nextTick();

				await this.selectNode(val);
				await this.$nextTick();

				this.pdfLoading = false;
			}
		},
		async zoom() {
			// Recompute crosshair position on zoom without altering scroll
			if (!this.selectedNode) return;

			// Sometimes the zoom triggers an erroneous page change; ensure we stay on the selectednode's page
			this.currentPage = this.selectedNode.page ?? 1;
			await this.$nextTick();
			await this.selectNode(this.selectedNode);

			// keep input in sync with programmatic zoom changes
			this.zoomInput = String(Math.round((this.zoom ?? 1) * 100));
		},
		sdeUuid() {
			this.debouncedReload();
		},
	},

	created() {
		if (!this.paperUuid && !this.sdeUuid) {
			console.error("DataViewer requires either paperUuid or sdeUuid.");
		}
		this.debouncedReload = _.debounce(this.reload, 50);
	},

	async mounted() {
		await this.reload();
	},

	methods: {
		async reload() {
			this.pdfLoading = true;
			this.pdfFiles = [];
			if (!this.paperUuid) {
				await this.getSde();
			}
			await this.getPaperData();
			this.pdfLoading = false;
		},
		...mapActions(useMainStore, ["showNotification"]),

		async initPdf(pdf) {
			this.pdf = pdf;
			this.totalPages = pdf.numPages || 1;
			await this.waitForCanvasStable(this.currentPageIndex);
			this.pdfLoading = false;
		},

		scrollPdf(offset) {
			const scroller = this.$el.querySelector(".pdf-vue3-scroller");
			const newTop = offset;
			const newLeft = scroller?.scrollLeft || 0;

			const topDiff = newTop - this.scrollTopPos;
			const leftDiff = newLeft - this.scrollLeftPos;

			if (topDiff) this.crosshairViewport.top -= topDiff;
			if (leftDiff) this.crosshairViewport.left -= leftDiff;

			this.scrollTopPos = newTop;
			this.scrollLeftPos = newLeft;
		},

		zoomIn() {
			this.zoom = Math.min(
				this.maxZoom,
				Number((this.zoom + this.zoomStep).toFixed(2)),
			);
			this.zoomInput = String(Math.round(this.zoom * 100));
		},
		zoomOut() {
			this.zoom = Math.max(
				this.minZoom,
				Number((this.zoom - this.zoomStep).toFixed(2)),
			);
			this.zoomInput = String(Math.round(this.zoom * 100));
		},

		applyZoomInput() {
			let raw = this.zoomInput;
			if (typeof raw !== "string") raw = String(raw ?? "");
			raw = raw.replace(/[^0-9]/g, "");
			let percent = Number(raw);
			if (!Number.isFinite(percent)) {
				percent = Math.round(this.zoom * 100);
			}
			percent = Math.min(500, Math.max(50, percent));
			this.zoomInput = String(percent);
			this.zoom = Number((percent / 100).toFixed(2));
		},

		async pageChange(pageNumber) {
			this.currentPage = pageNumber;
		},

		async selectNode(val) {
			await this.$nextTick();
			const canvas = this.$el.querySelector(".pdf-vue3-canvas-container");
			const scroller = this.$el.querySelector(".pdf-vue3-scroller");
			if (!canvas || !scroller) return;

			const targetPageIndex = this.currentPageIndex;
			const scale = await this.getPdfScale(canvas, targetPageIndex);
			const pagesOffset = this.getPagesOffset(canvas, targetPageIndex);

			// Calculate crosshair Y position and set Y scroll
			const pointScaledY = pagesOffset + val.y * scale.y;
			const pointScrollY = Math.max(
				0,
				pointScaledY - scroller.offsetHeight / 2,
			);

			const crosshairViewportY =
				pointScaledY - pointScrollY - this.crosshairSize / 2;
			scroller.scrollTop = this.scrollTopPos = pointScrollY;

			// Calculate crosshair X position and set X scroll
			const pointScaledX = val.x * scale.x;
			const maxScrollLeft = Math.max(
				0,
				(canvas?.offsetWidth || 0) - scroller.clientWidth,
			);
			const pointScrollX = Math.max(
				0,
				Math.min(pointScaledX - scroller.clientWidth / 2, maxScrollLeft),
			);
			const zoomOffsetX = parseFloat(getComputedStyle(canvas).marginLeft || 0);
			const crosshairViewportX =
				pointScaledX -
				pointScrollX -
				this.crosshairSize / 2 +
				zoomOffsetX +
				this.pageXMargin;

			scroller.scrollLeft = this.scrollLeftPos = pointScrollX;
			this.crosshairViewport = {
				left: crosshairViewportX,
				top: crosshairViewportY,
			};

			this.$refs.crosshairs.drawCrosshairs();
			this.$refs.crosshairs.playPulse(1);
		},

		// The x and y scale factors to go from WPD location to PDF location
		async getPdfScale(canvas, pageIndex) {
			const page = await this.pdf.getPage(this.currentPage);
			const pageViewport = await page.getViewport({ scale: 1 });
			const currentCanvas = canvas.children[pageIndex] || canvas.children[0];
			const h = currentCanvas?.offsetHeight || 0;
			const w = currentCanvas?.offsetWidth || 0;
			return {
				y: h / (pageViewport.height * this.wpdScaleRatio),
				x: w / (pageViewport.width * this.wpdScaleRatio),
			};
		},

		getPagesOffset(canvas, pageIndex) {
			const children = canvas?.children || [];
			return _.range(0, pageIndex).reduce((sum, i) => {
				return sum + children[i]?.offsetHeight + this.pageYMargin;
			}, 0);
		},

		async handleFileChange() {
			this.$refs.crosshairs.clearCrosshairs();
		},

		getFileProps(file) {
			return {
				disabled: file.disabled,
			};
		},
		async getSde() {
			return this.$http
				.get(`/data-entry/${this.sdeUuid}`)
				.then((response) => {
					this.sdePaperUuid = response.data.sde?.paper?.uuid;
				})
				.catch((error) => {
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				});
		},

		async getPaperData() {
			return this.$http
				.get(`/papers/${this.paperUuid ?? this.sdePaperUuid}`, {
					params: {
						dataFormat: "WPD",
					},
				})
				.then((response) => {
					let firstValidIndex = null;
					response.data.fileData.forEach((data, index) => {
						if (data.type === "application/pdf" || data.type.match("image.*")) {
							this.pdfFiles.push({
								data: data.data,
								type: data.type,
								uuid: data.uuid,
								name: data.name,
							});

							if (firstValidIndex === null) {
								firstValidIndex = index;
							}
						} else {
							this.pdfFiles.push({
								data: null,
								type: null,
								uuid: data.uuid,
								name: data.name,
							});
						}
					});

					this.currentFile = firstValidIndex !== null ? firstValidIndex : 0;
				})
				.catch((error) => {
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				});
		},

		// Hack - there is no event to notify when the PDF is finished rendering.
		// This uses observers to wait for the canvas to be stable for N ms (default 200).
		// Only needed for file changes when selecting a node in the tree.
		async waitForCanvasStable(pageIndex, stableMs = 200, timeoutMs = 2000) {
			const container = this.$el.querySelector(".pdf-vue3-canvas-container");
			if (!container) return;

			const getEl = () => container.children?.[pageIndex];

			// If already present and sized, we still wait for stability
			let el = getEl();

			return new Promise((resolve) => {
				let ro, mo, t, timeout;
				const cleanup = () => {
					clearTimeout(t);
					clearTimeout(timeout);
					ro?.disconnect();
					mo?.disconnect();
				};

				const armStableTimer = () => {
					clearTimeout(t);
					t = setTimeout(() => {
						cleanup();
						resolve(el);
					}, stableMs);
				};

				const onResize = () => {
					armStableTimer();
				};

				const startWatching = () => {
					if (!el) el = getEl();
					if (!el) return;
					ro = new ResizeObserver(onResize);
					ro.observe(el);
					armStableTimer();
				};

				if (!el) {
					mo = new MutationObserver(() => {
						el = getEl();
						if (el) {
							mo.disconnect();
							startWatching();
						}
					});
					mo.observe(container, { childList: true });
				} else {
					startWatching();
				}

				timeout = setTimeout(() => {
					cleanup();
					resolve(el);
				}, timeoutMs);
			});
		},
	},
};
</script>

<style lang="scss">
.zoom-input-wrap {
	width: 50px;
	flex: 0 0 50px;
}
.zoom-input {
	text-align: center;
	.v-field,
	.v-input__control,
	.v-field__field,
	.v-input__slot {
		max-width: 100%;
		width: 100%;
	}
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
</style>
